import React, { useState } from "react";
import { Search, Users as UsersIcon } from "lucide-react";
import { safeUserLabel } from "../utils/adminLevelUtils";
import { SkeletonBox } from "../../../components/SkeletonLoader";

const AVATAR_PALETTE = ["#2e7cba", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2"];

const initialsOf = (label) => {
  const parts = String(label || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const ROLE_COLORS = {
  superadmin: "#dc2626",
  admin: "#d97706",
  manager: "#7c3aed",
  user: "#2e7cba",
};

/**
 * Left panel: searchable list of the company's users.
 * - green dot + "Custom setup"    → user already has a personal level
 * - "Inherits {level}" caption    → user is on a shared level (selecting them
 *                                   asks for confirmation before cloning)
 */
const UserPicker = ({
  users, usersLoading, usersLoaded, userLevelMap,
  selectedUserId, onPickUser, theme, language, t,
}) => {
  const [search, setSearch] = useState("");
  // "all" | "has" (any level) | "custom" (personal level) | "none" (no level)
  const [levelFilter, setLevelFilter] = useState("all");

  const subColor = theme === "dark" ? "#94a3b8" : "#64748b";
  const headColor = theme === "dark" ? "#f1f5f9" : "#1e293b";

  const FILTER_OPTIONS = [
    { key: "all", label: t.filterAll },
    { key: "has", label: t.filterHasLevel },
    { key: "custom", label: t.filterCustomSetup },
    { key: "none", label: t.filterNoLevel },
  ];

  const matchesLevelFilter = (u) => {
    const info = userLevelMap[u.id];
    if (levelFilter === "has") return !!info;
    if (levelFilter === "custom") return !!info?.isPersonal;
    if (levelFilter === "none") return !info;
    return true;
  };

  const matchesSearch = (u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      safeUserLabel(u).toLowerCase().includes(q) ||
      String(u.email || "").toLowerCase().includes(q) ||
      String(u.role || "").toLowerCase().includes(q)
    );
  };

  const filtered = users.filter((u) => matchesLevelFilter(u) && matchesSearch(u));

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px",
      }}>
        <UsersIcon size={16} style={{ color: "#2e7cba" }} />
        <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: headColor }}>
          {t.teamMembers}
        </h4>
        {usersLoaded && (
          <span style={{
            fontSize: "11px", fontWeight: 600, padding: "2px 9px", borderRadius: "999px",
            background: theme === "dark" ? "rgba(46,124,186,0.2)" : "rgba(46,124,186,0.1)",
            color: "#2e7cba",
          }}>
            {users.length}
          </span>
        )}
      </div>

      {/* level filter chips (compose with the search below) */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "10px",
      }}>
        {FILTER_OPTIONS.map(({ key, label }) => {
          const active = levelFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setLevelFilter(key)}
              style={{
                padding: "5px 11px", borderRadius: "999px",
                fontSize: "11px", fontWeight: active ? 700 : 500,
                fontFamily: "inherit", cursor: "pointer",
                transition: "all 0.18s ease",
                color: active ? "#ffffff" : subColor,
                background: active
                  ? "#2e7cba"
                  : (theme === "dark" ? "rgba(148,163,184,0.1)" : "rgba(15,23,42,0.04)"),
                border: active
                  ? "1px solid #2e7cba"
                  : (theme === "dark"
                      ? "1px solid rgba(148,163,184,0.2)"
                      : "1px solid rgba(15,23,42,0.08)"),
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* search */}
      <div style={{ position: "relative", marginBottom: "12px" }}>
        <Search
          size={15}
          style={{
            position: "absolute", top: "50%", transform: "translateY(-50%)",
            insetInlineStart: "12px", color: subColor, pointerEvents: "none",
          }}
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.searchUsers}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "10px 12px",
            paddingInlineStart: "34px",
            borderRadius: "12px", fontSize: "13px", fontFamily: "inherit",
            border: theme === "dark"
              ? "1px solid rgba(148,163,184,0.25)"
              : "1px solid rgba(15,23,42,0.12)",
            background: theme === "dark" ? "rgba(8,10,15,0.6)" : "#ffffff",
            color: headColor,
            outline: "none",
          }}
        />
      </div>

      {/* result count (filter + search combined) */}
      {usersLoaded && !usersLoading && (
        <div style={{ fontSize: "11px", color: subColor, margin: "0 2px 8px" }}>
          {filtered.length} {t.resultsCount}
        </div>
      )}

      {/* list */}
      <div style={{
        overflowY: "auto", maxHeight: "560px",
        display: "flex", flexDirection: "column", gap: "6px",
        scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch",
      }}>
        {usersLoading ? (
          [1, 2, 3, 4, 5].map((i) => (
            <SkeletonBox key={i} width="100%" height="58px" style={{ borderRadius: "12px" }} />
          ))
        ) : !usersLoaded ? (
          <div style={{ fontSize: "12.5px", color: subColor, padding: "16px 8px", textAlign: "center" }}>
            {t.noUsersLoaded}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ fontSize: "12.5px", color: subColor, padding: "16px 8px", textAlign: "center" }}>
            {search || levelFilter !== "all" ? t.noSearchResults : t.noUsers}
          </div>
        ) : (
          filtered.slice(0, 400).map((user) => {
            const label = safeUserLabel(user);
            const levelInfo = userLevelMap[user.id];
            const isSelected = selectedUserId === user.id;
            const avatarColor = AVATAR_PALETTE[user.id % AVATAR_PALETTE.length];
            const roleColor = ROLE_COLORS[String(user.role || "").toLowerCase()] || "#64748b";
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => onPickUser(user)}
                style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", borderRadius: "12px",
                  cursor: "pointer", fontFamily: "inherit",
                  textAlign: "start", width: "100%",
                  transition: "all 0.18s ease",
                  border: isSelected
                    ? "1.5px solid #2e7cba"
                    : (theme === "dark"
                        ? "1px solid rgba(148,163,184,0.12)"
                        : "1px solid rgba(15,23,42,0.06)"),
                  background: isSelected
                    ? (theme === "dark" ? "rgba(46,124,186,0.18)" : "rgba(46,124,186,0.08)")
                    : (theme === "dark" ? "rgba(30,41,59,0.4)" : "rgba(255,255,255,0.7)"),
                }}
              >
                {/* avatar initials */}
                <span style={{
                  width: "36px", height: "36px", borderRadius: "12px", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "13px", fontWeight: 700, color: "#ffffff",
                  background: avatarColor,
                }}>
                  {initialsOf(label)}
                </span>

                <span style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    <span style={{
                      fontSize: "13px", fontWeight: 600, color: headColor,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {label}
                    </span>
                    {/* configured dot: user already has a personal configuration */}
                    {levelInfo?.isPersonal && (
                      <span
                        title={t.customSetup}
                        style={{
                          width: "8px", height: "8px", borderRadius: "50%",
                          background: "#10b981", flexShrink: 0,
                          boxShadow: "0 0 0 3px rgba(16,185,129,0.2)",
                        }}
                      />
                    )}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    {user.role && (
                      <span style={{
                        fontSize: "10px", fontWeight: 700, padding: "1px 7px",
                        borderRadius: "999px", textTransform: "uppercase",
                        letterSpacing: "0.04em", flexShrink: 0,
                        color: roleColor,
                        background: theme === "dark" ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.05)",
                      }}>
                        {user.role}
                      </span>
                    )}
                    <span style={{
                      fontSize: "11px", color: subColor,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {levelInfo?.isPersonal
                        ? t.customSetup
                        : levelInfo?.levelName
                          ? `${t.inheritsPrefix} ${levelInfo.levelName}`
                          : t.noLevelYet}
                    </span>
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UserPicker;
